import { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useAdmin } from '../context/AdminContext';
import { t } from '../lib/translations';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Trash2, ArrowLeft, FileText, Calendar, Lock, Eye, EyeOff } from 'lucide-react';
import { Input } from '../components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function AdminLoginGate() {
  const { language } = useLanguage();
  const { login } = useAdmin();
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password.trim()) return;
    setLoading(true);
    setError('');
    try {
      await login(password);
    } catch {
      setError(language === 'pl' ? 'Nieprawidłowe hasło' : 'Invalid password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-20" data-testid="admin-login-gate">
      <div className="bg-white rounded-2xl border border-slate-200 p-8 shadow-lg">
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-xl bg-[#1B3A7B] text-white flex items-center justify-center mx-auto mb-4">
            <Lock className="w-6 h-6" />
          </div>
          <h2 className="font-heading text-2xl font-bold text-[#1B3A7B]">
            {language === 'pl' ? 'Panel administratora' : 'Admin Panel'}
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            {language === 'pl' ? 'Podaj hasło, aby uzyskać dostęp' : 'Enter password to access'}
          </p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="relative">
            <Input
              type={showPw ? 'text' : 'password'}
              placeholder={language === 'pl' ? 'Hasło administratora' : 'Admin password'}
              value={password}
              onChange={(e) => { setPassword(e.target.value); setError(''); }}
              className={`h-12 pr-10 border-slate-300 focus:border-[#1B3A7B] focus:ring-[#1B3A7B] ${error ? 'border-red-400' : ''}`}
              data-testid="admin-password-input"
              autoFocus
            />
            <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
              {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          {error && <p className="text-red-500 text-sm font-medium">{error}</p>}
          <button
            type="submit"
            disabled={loading || !password.trim()}
            className={`w-full h-12 rounded-md font-semibold text-base transition-all ${loading || !password.trim() ? 'bg-slate-200 text-slate-400 cursor-not-allowed' : 'bg-[#1B3A7B] hover:bg-[#142d63] text-white shadow-md hover:shadow-lg'}`}
            data-testid="admin-login-btn"
          >
            {loading ? (language === 'pl' ? 'Logowanie...' : 'Logging in...') : (language === 'pl' ? 'Zaloguj się' : 'Log in')}
          </button>
        </form>
      </div>
    </div>
  );
}

export default function QuoteHistory() {
  const { language } = useLanguage();
  const { isAdmin, token, loading: authLoading } = useAdmin();
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  const authHeaders = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (isAdmin && token) fetchQuotes();
  }, [isAdmin, token]);

  const fetchQuotes = async () => {
    try {
      const res = await axios.get(`${API}/quotes`, { headers: authHeaders });
      setQuotes(res.data);
    } catch {
      toast.error(t('common.error', language));
    } finally {
      setLoading(false);
    }
  };

  const deleteQuote = async (id) => {
    try {
      await axios.delete(`${API}/quotes/${id}`, { headers: authHeaders });
      setQuotes(prev => prev.filter(q => q.id !== id));
      toast.success(t('common.deleted', language));
    } catch {
      toast.error(t('common.error', language));
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString(language === 'pl' ? 'pl-PL' : 'en-GB', {
      year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };

  if (authLoading) {
    return (
      <div className="flex justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#1B3A7B] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAdmin) {
    return <AdminLoginGate />;
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="quote-history-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#1B3A7B]">
            {t('history.title', language)}
          </h1>
          <p className="text-slate-500 mt-1">{t('history.subtitle', language)}</p>
        </div>
        <button
          onClick={() => navigate('/')}
          className="px-4 py-2 rounded-md text-sm font-medium border-2 border-[#1B3A7B] text-[#1B3A7B] hover:bg-[#1B3A7B] hover:text-white transition-all flex items-center gap-1.5"
          data-testid="back-to-calc-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('history.backToCalc', language)}
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-2 border-[#1B3A7B] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : quotes.length === 0 ? (
        <div className="text-center py-20">
          <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500 text-lg">{t('history.empty', language)}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {quotes.map((quote) => (
            <div
              key={quote.id}
              className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm hover:shadow-md transition-shadow"
              data-testid={`quote-item-${quote.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="font-mono-price text-2xl font-bold text-[#1B3A7B]">
                      {quote.monthly_total} {t('common.currency', language)}
                    </span>
                    <span className="text-xs text-slate-500">/ {t('common.monthly', language)}</span>
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-600">
                    <span>{t(`step1.${quote.accounting_form}`, language)}</span>
                    <span>{t(`step1.entities.${quote.legal_entity}`, language)}</span>
                    <span>{quote.document_count} {language === 'pl' ? 'poz.' : 'entries'}</span>
                    {quote.employee_count > 0 && (
                      <span>{quote.employee_count} {language === 'pl' ? 'prac.' : 'emp.'}</span>
                    )}
                  </div>
                  {quote.client_name && (
                    <p className="text-sm text-slate-500 mt-1">{quote.client_name}</p>
                  )}
                  <div className="flex items-center gap-1.5 mt-2 text-xs text-slate-400">
                    <Calendar className="w-3.5 h-3.5" />
                    {formatDate(quote.created_at)}
                  </div>
                </div>
                <button
                  onClick={() => deleteQuote(quote.id)}
                  className="w-9 h-9 rounded-lg flex items-center justify-center text-slate-400 hover:bg-red-50 hover:text-red-500 transition-colors"
                  data-testid={`delete-quote-${quote.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
